import pt from '../locales/pt';
import { debtTypeLabel } from '../components/liabilities/debtTypes';
import { toNumber } from '../utils/number';
import { DISPLAY_GROUP_ORDER, resolveFormTypeFromCategory } from './accountsDisplay';

/**
 * Consecutive periods with net worth growth (newest first), mirroring backend logic.
 */
export function computeGrowthStreak(timeline = []) {
  let count = 0;
  for (let i = timeline.length - 1; i >= 0; i -= 1) {
    const change = toNumber(timeline[i]?.net_worth_change_eur);
    if (change > 0) {
      count += 1;
    } else {
      break;
    }
  }
  return count;
}

function groupLabel(groupId) {
  return pt.accounts.form.typeLabels[groupId] || groupId;
}

function buildAccountSubtitle(names, maxVisible = 3) {
  if (!names.length) return '';
  if (names.length <= maxVisible) {
    return names.join(' · ');
  }
  const visible = names.slice(0, maxVisible);
  const rest = names.length - maxVisible;
  return `${visible.join(' · ')} · +${rest}`;
}

/**
 * @param {{
 *   accountSnapshots?: Array<{ account: string|number, balance_eur?: number|string, account_name?: string }>,
 *   accountIndex: Map<string|number, { category?: string, name?: string, is_active?: boolean }>,
 *   liabilitySnapshots?: Array<{ id: string|number, liability?: string|number, liability_name?: string, remaining_balance_eur?: number|string, liability_type?: string }>,
 * }} params
 */
export function buildPatrimonioComposition({
  accountSnapshots = [],
  accountIndex,
  liabilitySnapshots = [],
}) {
  const groupAccounts = new Map(DISPLAY_GROUP_ORDER.map((id) => [id, []]));

  for (const row of accountSnapshots) {
    const account = accountIndex.get(row.account);
    if (!account?.is_active) continue;

    const balance = toNumber(row.balance_eur);
    if (balance <= 0) continue;

    const groupId = resolveFormTypeFromCategory(account.category || 'other');
    const list = groupAccounts.get(groupId) || [];
    list.push({ name: row.account_name || account.name || '', balance });
    groupAccounts.set(groupId, list);
  }

  const assetGroups = DISPLAY_GROUP_ORDER.map((groupId) => {
    const accounts = groupAccounts.get(groupId) || [];
    const value = accounts.reduce((sum, item) => sum + item.balance, 0);
    const names = accounts
      .map((item) => item.name)
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b, 'pt'));

    return {
      id: groupId,
      label: groupLabel(groupId),
      value,
      subtitle: buildAccountSubtitle(names),
      accountCount: accounts.length,
    };
  }).filter((group) => group.value > 0);

  const totalAssets = assetGroups.reduce((sum, group) => sum + group.value, 0);

  const assetGroupsWithPct = assetGroups.map((group) => ({
    ...group,
    pct: totalAssets > 0 ? Math.round((group.value / totalAssets) * 100) : null,
  }));

  const liabilityItems = liabilitySnapshots
    .map((row) => {
      const balance = toNumber(row.remaining_balance_eur);
      if (balance <= 0) return null;

      const subtitleParts = [];
      if (row.liability_type) {
        subtitleParts.push(debtTypeLabel(row.liability_type));
      }

      return {
        id: row.id,
        name: row.liability_name || String(row.liability || ''),
        subtitle: subtitleParts.join(' · '),
        value: balance,
      };
    })
    .filter(Boolean)
    .sort((a, b) => b.value - a.value);

  const totalLiabilities = liabilityItems.reduce((sum, item) => sum + item.value, 0);

  return {
    assetGroups: assetGroupsWithPct,
    liabilityItems,
    totalAssets,
    totalLiabilities,
  };
}

export function formatSelfieShortDate(isoDate, locale = 'pt-PT') {
  if (!isoDate) return '';
  const date = new Date(`${isoDate}T12:00:00`);
  return new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short' }).format(date);
}
